if (!customElements.get('rpg-loose-game-checkout-preview')) {
  class RpgLooseGameCheckoutPreview extends HTMLElement {
    connectedCallback() {
      this.qualifier = this.dataset.qualifies === 'true';
      this.baseTotal = Number(this.dataset.cartTotal || 0);
      this.baseOriginalTotal = Number(this.dataset.cartOriginalTotal || this.baseTotal);
      this.baseSavings = Number(this.dataset.cartSavings || Math.max(0, this.baseOriginalTotal - this.baseTotal));
      this.platform = this.dataset.platform || 'Your system';
      this.platformHandle = this.handleize(this.platform);
      this.platformCount = Math.max(1, Number(this.dataset.platformCount || 1));
      this.maxSets = 5 + (Math.max(0, this.platformCount - 1) * 2);
      this.selected = new Set();
      this.cartVariantIds = new Set(String(this.dataset.cartVariantIds || '').split('||').filter(Boolean));
      this.page = 0;
      this.maxRecommendations = this.maxSets * 5;
      this.orderPage = 0;
      this.orderPageSize = 4;
      this.cartGenres = String(this.dataset.cartGenres || '')
        .split('||')
        .map((genre) => this.handleize(genre))
        .filter(Boolean);
      this.cartGenresByPlatform = new Map();
      const preferenceSeed = this.querySelector('[data-rpg-cart-preferences]');
      if (preferenceSeed) {
        try {
          const preferences = JSON.parse(preferenceSeed.textContent || '[]');
          preferences.forEach((preference) => {
            const platformHandle = this.handleize(preference.platform);
            if (!platformHandle) return;
            const genres = (Array.isArray(preference.genres) ? preference.genres : [])
              .map((genre) => this.handleize(genre))
              .filter(Boolean);
            const knownGenres = this.cartGenresByPlatform.get(platformHandle) || new Set();
            genres.forEach((genre) => knownGenres.add(genre));
            this.cartGenresByPlatform.set(platformHandle, knownGenres);
          });
        } catch (error) {
          // The combined cart genre list remains the safe ranking fallback.
        }
      }
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.revealedPrices = new Set();
      this.justAddedId = '';
      try {
        const recentAdd = JSON.parse(window.sessionStorage.getItem('rpg-super-deal-added') || 'null');
        if (recentAdd && Date.now() - Number(recentAdd.addedAt || 0) < 15000
          && this.cartVariantIds.has(String(recentAdd.variantId || ''))) {
          this.justAddedId = String(recentAdd.variantId);
        }
        window.sessionStorage.removeItem('rpg-super-deal-added');
      } catch (error) {
        // A blocked session store should not prevent the cart from rendering.
      }
      this.displayedSavings = this.justAddedId ? 0 : this.baseSavings;
      this.moneyAnimations = new Map();
      this.boundFitBadgeLabels = () => this.fitBadgeLabels();
      this.currentDealPageSize = this.dealPageSize();
      this.boundHandleResize = () => {
        const nextPageSize = this.dealPageSize();
        if (nextPageSize !== this.currentDealPageSize) {
          this.currentDealPageSize = nextPageSize;
          this.page = 0;
          this.render();
          return;
        }
        this.fitBadgeLabels();
      };
      this.games = [];
      const inventorySeed = this.querySelector('[data-rpg-deal-inventory]');
      if (inventorySeed) {
        try {
          const seededGames = JSON.parse(inventorySeed.textContent || '[]');
          if (Array.isArray(seededGames) && seededGames.length) {
            this.games = this.rankedGames(seededGames).slice(0, this.maxRecommendations);
          }
        } catch (error) {
          // Keep the local preview set when Liquid inventory data is unavailable.
        }
      }
      this.items = this.querySelector('[data-rpg-checkout-items]');
      if (this.justAddedId && this.items) {
        this.items.querySelector(`[data-rpg-cart-variant="${this.justAddedId}"]`)?.classList.add('is-just-added');
      }
      this.baseItems = this.items ? [...this.items.children].map((item) => item.outerHTML) : [];
      this.bindPagination();
      this.render();
      window.addEventListener('resize', this.boundHandleResize, { passive: true });
      document.fonts?.ready.then(this.boundFitBadgeLabels);
      if (!inventorySeed || !this.games.length) this.loadInventoryGames();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.boundHandleResize);
    }

    money(cents) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
    }

    previewPrice(game) {
      return Math.max(game.offer, Math.ceil(game.retail * 0.55));
    }

    handleize(value) {
      return String(value || '')
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    rankedGames(games) {
      const gradeScores = { new: 6, excellent: 5, great: 4, good: 3, fair: 2, poor: 1 };
      const genreAffinity = {
        action: ['adventure', 'arcade', 'fighting', 'hack-and-slash-beat-em-up', 'platform', 'shooter'],
        adventure: ['action', 'platform', 'role-playing-rpg', 'puzzle'],
        arcade: ['action', 'platform', 'racing', 'sport'],
        fighting: ['action', 'arcade', 'hack-and-slash-beat-em-up'],
        platform: ['adventure', 'arcade', 'racing'],
        puzzle: ['card-and-board-game', 'quiz-trivia', 'strategy'],
        racing: ['arcade', 'simulator', 'sport'],
        'role-playing-rpg': ['adventure', 'strategy', 'turn-based-strategy-tbs'],
        shooter: ['action', 'arcade', 'simulator', 'tactical'],
        simulator: ['racing', 'shooter', 'sport', 'strategy'],
        sport: ['arcade', 'racing', 'simulator'],
        strategy: ['puzzle', 'role-playing-rpg', 'turn-based-strategy-tbs']
      };
      const score = (game) => {
        const discount = game.retail ? (game.retail - this.previewPrice(game)) / game.retail : 0;
        const grade = gradeScores[this.handleize(game.grade)] || 0;
        const merchandising = Math.max(0, 100 - Number(game.catalogRank || 0));
        const gameGenres = (Array.isArray(game.genres) ? game.genres : String(game.genres || '').split('||'))
          .map((genre) => this.handleize(genre))
          .filter(Boolean);
        const platformGenres = this.cartGenresByPlatform.get(this.handleize(game.platform));
        const targetGenres = platformGenres?.size ? [...platformGenres] : this.cartGenres;
        const sharedGenres = gameGenres.filter((genre) => targetGenres.includes(genre)).length;
        const relatedGenres = gameGenres.filter((genre) => targetGenres.some((cartGenre) => (genreAffinity[cartGenre] || []).includes(genre))).length;
        return (sharedGenres * 1200) + (relatedGenres * 700) + (discount * 600) + (grade * 40) + merchandising;
      };
      const scoredGames = [...games]
        .map((game) => ({ game, score: score(game) }))
        .sort((left, right) => right.score - left.score);
      const platformQueues = new Map();
      scoredGames.forEach((entry) => {
        const platformHandle = this.handleize(entry.game.platform || this.platform);
        if (!platformQueues.has(platformHandle)) platformQueues.set(platformHandle, []);
        platformQueues.get(platformHandle).push(entry);
      });
      if (platformQueues.size < 2) return scoredGames.map((entry) => entry.game);

      const platformOrder = [...platformQueues.keys()]
        .sort((left, right) => (platformQueues.get(right)[0]?.score || 0) - (platformQueues.get(left)[0]?.score || 0));
      const mixedGames = [];
      let hasCandidates = true;
      while (hasCandidates) {
        hasCandidates = false;
        platformOrder.forEach((platformHandle) => {
          const next = platformQueues.get(platformHandle).shift();
          if (!next) return;
          mixedGames.push(next.game);
          hasCandidates = true;
        });
      }
      return mixedGames;
    }

    async loadInventoryGames() {
      if (!this.dataset.productsUrl) return;
      try {
        const response = await fetch(this.dataset.productsUrl, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const payload = await response.json();
        const inventoryGames = [];
        let catalogRank = 0;
        for (const product of payload.products || []) {
          if (String(product.product_type || '').toLowerCase() !== 'game') continue;
          for (const variant of product.variants || []) {
            const variantTitle = String(variant.title || '');
            const retail = Math.round(Number(variant.price || 0) * 100);
            if (!variant.available || retail >= 500 || !/game only/i.test(variantTitle)) continue;
            const rawStock = Number(variant.inventory_quantity);
            inventoryGames.push({
              id: String(variant.id),
              title: product.title,
              retail,
              offer: 0,
              image: product.images?.[0]?.src || '',
              grade: variantTitle.split('·')[0]?.trim() || 'Fair',
              completeness: 'Disc Only',
              platform: this.platform,
              platformHandle: this.platformHandle,
              catalogRank,
              stock: Number.isFinite(rawStock) ? rawStock : null
            });
            catalogRank += 1;
          }
        }
        if (inventoryGames.length) {
          this.games = this.rankedGames(inventoryGames).slice(0, this.maxRecommendations);
          this.page = 0;
          this.render();
        }
      } catch (error) {
        // Keep the local preview set when public inventory cannot be loaded.
      }
    }

    artwork(game) {
      if (game.image) {
        return `<img class="rpg-loose-checkout__artwork" src="${this.escapeHtml(game.image)}" alt="${this.escapeHtml(game.title)}" loading="lazy">`;
      }
      return '<span class="rpg-loose-checkout__artwork-placeholder" aria-hidden="true">💿</span>';
    }

    platformBadgeLabel(platform) {
      const labels = {
        'Nintendo Entertainment System': 'NES',
        'Nintendo NES': 'NES',
        'Super Nintendo': 'SNES',
        'Super Nintendo Entertainment System': 'SNES',
        'Nintendo 64': 'N64',
        'Nintendo GameCube': 'GameCube',
        'Game Boy Color': 'GBC',
        'Game Boy Advance': 'GBA',
        'Nintendo DS': 'DS',
        'Nintendo 3DS': '3DS',
        'Nintendo Wii': 'Wii',
        'Nintendo Wii U': 'Wii U',
        'Nintendo Switch': 'Switch',
        'Nintendo Switch 2': 'Switch 2',
        PlayStation: 'PS1',
        'PlayStation 1': 'PS1',
        'PlayStation 2': 'PS2',
        'PlayStation 3': 'PS3',
        'PlayStation 4': 'PS4',
        'PlayStation 5': 'PS5',
        'PlayStation Portable': 'PSP',
        'PlayStation Vita': 'PS Vita'
      };
      return labels[platform] || platform;
    }

    compactCompletenessLabel(completeness) {
      const normalized = String(completeness || '').toLowerCase().replace('.', '').trim();
      if (normalized === 'disc only') return 'DISC';
      if (normalized === 'cart only' || normalized === 'cartridge only') return 'CART';
      return completeness;
    }

    responsiveBadgeLabels(full, compact) {
      return `<span class="rpg-responsive-badge-label rpg-responsive-badge-label--full rpg-loose-checkout__badge-label">${this.escapeHtml(full)}</span><span class="rpg-responsive-badge-label rpg-responsive-badge-label--compact rpg-loose-checkout__badge-label">${this.escapeHtml(compact)}</span>`;
    }

    dealPageSize() {
      return window.matchMedia('(max-width: 760px)').matches ? 2 : 5;
    }

    dealGames() {
      return this.games.slice(0, this.maxSets * this.dealPageSize());
    }

    badges(game) {
      const grade = game.grade || 'Fair';
      const completeness = game.completeness || 'Disc Only';
      const platform = game.platform || this.platform;
      const platformHandle = game.platformHandle || this.handleize(platform);
      const stock = Number(game.stock);
      const stockBadge = Number.isFinite(stock) && stock > 0 && stock <= 2
        ? `<span class="rpg-loose-checkout__stock-badge"><span class="rpg-loose-checkout__badge-label">${stock} LEFT</span></span>`
        : '';
      return `<div class="rpg-loose-checkout__badges" aria-label="Copy details">
        <span class="rpg-loose-checkout__platform-badge rpg-console-color rpg-console-color--${this.escapeHtml(platformHandle)} rpg-console-badge">${this.responsiveBadgeLabels(this.platformBadgeLabel(platform), this.platformBadgeLabel(platform))}</span>
        <span class="rpg-game-card__badge rpg-game-card__badge--included rpg-game-card__badge--included-game-only rpg-game-card__badge--media-disc">${this.responsiveBadgeLabels(completeness, this.compactCompletenessLabel(completeness))}</span>
        <span class="rpg-game-card__badge rpg-game-card__badge--grade rpg-game-card__badge--${this.handleize(grade)}"><span class="rpg-loose-checkout__badge-label">${this.escapeHtml(grade)}</span></span>
        ${stockBadge}
      </div>`;
    }

    fitBadgeLabels() {
      window.requestAnimationFrame(() => {
        this.querySelectorAll('.rpg-loose-checkout__badges').forEach((group) => {
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          const entries = [...group.children].map((badge) => {
            const label = [...badge.querySelectorAll('.rpg-loose-checkout__badge-label')]
              .find((candidate) => window.getComputedStyle(candidate).display !== 'none');
            if (!label) return null;
            label.style.setProperty('--rpg-badge-scale-x', '1');
            badge.style.removeProperty('width');
            const badgeStyles = window.getComputedStyle(badge);
            const labelStyles = window.getComputedStyle(label);
            if (context) context.font = labelStyles.font;
            const measured = (text) => context ? context.measureText(text).width : 0;
            const natural = label.getBoundingClientRect().width;
            const maximum = Math.max(1, measured('Game & Box'));
            const chrome = [
              badgeStyles.paddingLeft,
              badgeStyles.paddingRight,
              badgeStyles.borderLeftWidth,
              badgeStyles.borderRightWidth
            ].reduce((total, value) => total + (parseFloat(value) || 0), 0);
            const width = Math.ceil(Math.min(natural, maximum) + chrome);
            badge.style.width = `${width}px`;
            const scale = natural > maximum ? (maximum * .88) / natural : 1;
            label.style.setProperty('--rpg-badge-scale-x', String(Math.max(.35, Math.min(1, scale))));
            let fittedChrome = chrome;
            if (window.matchMedia('(max-width: 760px)').matches) {
              const badgeRect = badge.getBoundingClientRect();
              const labelRect = label.getBoundingClientRect();
              const verticalSpace = Math.max(0, (badgeRect.height - labelRect.height) / 2);
              const horizontalSpace = verticalSpace * .75;
              const fittedWidth = labelRect.width + (horizontalSpace * 2);
              badge.style.width = `${fittedWidth}px`;
              fittedChrome = fittedWidth - labelRect.width;
            }
            return { badge, label, natural, chrome: fittedChrome };
          }).filter(Boolean);

          const gap = parseFloat(window.getComputedStyle(group).columnGap || 0);
          const total = entries.reduce((sum, entry) => sum + entry.badge.getBoundingClientRect().width, 0)
            + (Math.max(0, entries.length - 1) * gap);
          if (total > group.clientWidth && entries.length) {
            const availableForBadges = Math.max(1, group.clientWidth - (Math.max(0, entries.length - 1) * gap));
            const badgeWidthTotal = total - (Math.max(0, entries.length - 1) * gap);
            const ratio = availableForBadges / badgeWidthTotal;
            entries.forEach((entry) => {
              const width = Math.max(entry.chrome + 1, entry.badge.getBoundingClientRect().width * ratio);
              entry.badge.style.width = `${width}px`;
              const labelRoom = Math.max(1, width - entry.chrome) * .88;
              const scale = Math.min(1, labelRoom / entry.natural);
              entry.label.style.setProperty('--rpg-badge-scale-x', String(Math.max(.35, scale)));
            });
          }
        });
      });
    }

    animateMoney(element, target, key) {
      if (!element) return;
      const start = Number(element.dataset.rpgCents ?? target);
      element.dataset.rpgCents = String(target);
      const activeFrame = this.moneyAnimations.get(key);
      if (activeFrame) cancelAnimationFrame(activeFrame);
      if (this.prefersReducedMotion || start === target) {
        element.textContent = this.money(target);
        return;
      }
      const startedAt = performance.now();
      const tick = (now) => {
        const progress = Math.min(1, (now - startedAt) / 450);
        const eased = 1 - Math.pow(1 - progress, 3);
        element.textContent = this.money(Math.round(start + ((target - start) * eased)));
        if (progress < 1) this.moneyAnimations.set(key, requestAnimationFrame(tick));
        else this.moneyAnimations.delete(key);
      };
      this.moneyAnimations.set(key, requestAnimationFrame(tick));
    }

    bindCartRemovals() {
      this.querySelectorAll('[data-rpg-remove-line]').forEach((button) => button.addEventListener('click', async () => {
        button.disabled = true;
        try {
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ line: Number(button.dataset.rpgRemoveLine), quantity: 0 })
          });
          if (!response.ok) throw new Error('Cart item could not be removed.');
          window.location.reload();
        } catch (error) {
          button.disabled = false;
          window.alert(error.message || 'Cart item could not be removed.');
        }
      }));
    }

    bindPagination() {
      this.querySelector('[data-rpg-loose-previous]')?.addEventListener('click', () => {
        this.page = Math.max(0, this.page - 1);
        this.render();
      });
      this.querySelector('[data-rpg-loose-next]')?.addEventListener('click', () => {
        const pageCount = Math.max(1, Math.ceil(this.dealGames().length / this.dealPageSize()));
        this.page = Math.min(pageCount - 1, this.page + 1);
        this.render();
      });
      this.querySelector('[data-rpg-order-previous]')?.addEventListener('click', () => {
        this.orderPage = Math.max(0, this.orderPage - 1);
        this.render();
      });
      this.querySelector('[data-rpg-order-next]')?.addEventListener('click', () => {
        const selectedCount = this.games.filter((game) => this.selected.has(game.id)).length;
        const pageCount = Math.max(1, Math.ceil((this.baseItems.length + selectedCount) / this.orderPageSize));
        this.orderPage = Math.min(pageCount - 1, this.orderPage + 1);
        this.render();
      });
    }

    revealCurrentPrices(pageGames) {
      const unrevealed = pageGames.filter((game) => !this.revealedPrices.has(game.id));
      if (!unrevealed.length) return;
      if (this.prefersReducedMotion) {
        unrevealed.forEach((game) => this.revealedPrices.add(game.id));
        return;
      }
      requestAnimationFrame(() => {
        unrevealed.forEach((game) => {
          this.querySelector(`[data-rpg-price-reveal="${game.id}"]`)?.classList.add('is-visible');
          this.revealedPrices.add(game.id);
        });
      });
    }

    render() {
      const games = this.querySelector('[data-rpg-loose-games]');
      const selectedGames = this.games.filter((game) => this.selected.has(game.id));
      const discTotal = selectedGames.reduce((sum, game) => sum + this.previewPrice(game), 0);
      const trashIcon = '<svg width="20" height="20" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M14 3h-3.53a3.07 3.07 0 0 0-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 0 0 5.53 3H2a.5.5 0 0 0 0 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 0 0 .5-.5V4H14a.5.5 0 0 0 0-1zM6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02zm4.84 11.52h-7.5V4h7.5v9.5z" fill="currentColor"/><path d="M6.55 5.25a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5zm2.9 0a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5z" fill="currentColor"/></svg>';
      const orderItems = [
        ...this.baseItems,
        ...selectedGames.map((game) => `<article class="rpg-loose-checkout__item rpg-loose-checkout__item--deal"><div class="rpg-loose-checkout__item-card">${this.artwork(game)}<div class="rpg-loose-checkout__listing-copy"><strong>${this.escapeHtml(game.title)}</strong>${this.badges(game)}</div><strong>${this.money(this.previewPrice(game))}</strong></div><button class="rpg-loose-checkout__remove" type="button" data-rpg-remove-loose="${game.id}" aria-label="Remove ${this.escapeHtml(game.title)} from your order">${trashIcon}</button></article>`)
      ];
      const orderPageCount = Math.max(1, Math.ceil(orderItems.length / this.orderPageSize));
      this.orderPage = Math.min(this.orderPage, orderPageCount - 1);
      if (this.items) {
        const orderStart = this.orderPage * this.orderPageSize;
        this.items.innerHTML = orderItems.slice(orderStart, orderStart + this.orderPageSize).join('');
      }
      this.bindCartRemovals();

      const orderPager = this.querySelector('[data-rpg-order-pager]');
      if (orderPager) {
        orderPager.hidden = orderPageCount < 2;
        this.querySelector('[data-rpg-order-page]').textContent = `${this.orderPage + 1} / ${orderPageCount}`;
        this.querySelector('[data-rpg-order-previous]').disabled = this.orderPage === 0;
        this.querySelector('[data-rpg-order-next]').disabled = this.orderPage >= orderPageCount - 1;
      }

      const dealPageSize = this.dealPageSize();
      const dealGames = this.dealGames();
      const pageCount = Math.max(1, Math.ceil(dealGames.length / dealPageSize));
      this.page = Math.min(Math.max(0, this.page), pageCount - 1);
      let pageGames = [];
      if (games && this.qualifier) {
        const start = this.page * dealPageSize;
        pageGames = dealGames.slice(start, start + dealPageSize);
        games.innerHTML = pageGames.map((game) => {
          const selected = this.selected.has(game.id) || this.cartVariantIds.has(String(game.id));
          const isJustAdded = game.id === this.justAddedId;
          const isUnrevealed = !this.revealedPrices.has(game.id) && !this.prefersReducedMotion;
          return `<article class="rpg-loose-offer-preview__game${isJustAdded ? ' is-just-added' : ''}" data-rpg-deal-row="${game.id}">
            ${this.artwork(game)}
            <div class="rpg-loose-checkout__listing-copy"><strong>${this.escapeHtml(game.title)}</strong>${this.badges(game)}</div>
            <div class="rpg-loose-offer-preview__price"><s>${this.money(game.retail)}</s><strong class="rpg-loose-offer-preview__deal-price${isUnrevealed ? ' is-revealing' : ''}${isJustAdded ? ' is-pulsing' : ''}" data-rpg-price-reveal="${game.id}">${this.money(this.previewPrice(game))}</strong></div>
            <button type="button" data-rpg-add-loose="${game.id}" ${selected ? 'disabled' : ''}>${selected ? '<span aria-hidden="true">✓</span> Added' : 'Add'}</button>
          </article>`;
        }).join('');
        this.revealCurrentPrices(pageGames);
      }

      const pager = this.querySelector('[data-rpg-loose-pager]');
      if (pager) {
        pager.hidden = pageCount < 2;
        this.querySelector('[data-rpg-loose-page]').textContent = `${this.page + 1} / ${pageCount}`;
        this.querySelector('[data-rpg-loose-previous]').disabled = this.page === 0;
        this.querySelector('[data-rpg-loose-next]').disabled = this.page >= pageCount - 1;
      }

      const subtotal = this.baseTotal + discTotal;
      this.animateMoney(this.querySelector('[data-rpg-subtotal]'), subtotal, 'subtotal');
      this.animateMoney(this.querySelector('[data-rpg-total]'), subtotal, 'total');
      const savings = this.baseSavings + selectedGames.reduce((sum, game) => sum + game.retail - this.previewPrice(game), 0);
      const savingsRow = this.querySelector('[data-rpg-savings]');
      if (savingsRow) {
        const savingsIncreased = savings > this.displayedSavings;
        savingsRow.hidden = savings === 0;
        savingsRow.innerHTML = savings ? '<span>Super Deal Savings</span><strong data-rpg-savings-value></strong>' : '';
        if (savings) {
          const savingsValue = savingsRow.querySelector('[data-rpg-savings-value]');
          savingsValue.dataset.rpgCents = String(this.displayedSavings);
          this.animateMoney(savingsValue, savings, 'savings');
        }
        savingsRow.classList.toggle('is-bright', savingsIncreased && !this.prefersReducedMotion);
        if (savingsIncreased && !this.prefersReducedMotion) window.setTimeout(() => savingsRow.classList.remove('is-bright'), 700);
      }
      this.displayedSavings = savings;
      this.fitBadgeLabels();

      this.querySelectorAll('[data-rpg-remove-loose]').forEach((button) => button.addEventListener('click', () => {
        this.selected.delete(button.dataset.rpgRemoveLoose);
        this.justAddedId = '';
        this.render();
      }));
      this.querySelectorAll('[data-rpg-add-loose]').forEach((button) => button.addEventListener('click', async () => {
        const addedId = button.dataset.rpgAddLoose;
        const game = this.games.find((candidate) => String(candidate.id) === String(addedId));
        if (!game || this.cartVariantIds.has(String(addedId))) return;
        button.disabled = true;
        button.textContent = 'Adding…';
        try {
          const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [{
                id: Number(addedId),
                quantity: 1,
                properties: {
                  '_RPG Bundle ID': `super-disc-${game.platformHandle || this.handleize(game.platform || this.platform)}`,
                  '_RPG Bundle Role': 'discounted-disc',
                  '_RPG Bundle Platform': game.platform || this.platform,
                  '_RPG Bundle Discount': '45%'
                }
              }]
            })
          });
          if (!response.ok) {
            const payload = await response.json().catch(() => ({}));
            throw new Error(payload.description || payload.message || 'This disc could not be added to your cart.');
          }
          try {
            window.sessionStorage.setItem('rpg-super-deal-added', JSON.stringify({ variantId: String(addedId), addedAt: Date.now() }));
          } catch (error) {
            // Continue to the authoritative server-rendered cart when storage is unavailable.
          }
          window.location.reload();
        } catch (error) {
          button.disabled = false;
          button.textContent = 'Add';
          window.alert(error.message || 'This disc could not be added to your cart.');
        }
      }));
    }
  }

  customElements.define('rpg-loose-game-checkout-preview', RpgLooseGameCheckoutPreview);
}
